import React from 'react';
import Card from 'lib/components/Card';
import { MailDelivery } from 'lib/types';
import {
  computeNoticeAddOnPricing,
  deriveNoticeAddOnPricingContext,
  expandNoticeAddOnsToLineItems
} from 'lib/pricing/noticeAddOns/shared';
import { NoticeAddOnPricingConfig } from 'lib/pricing/noticeAddOns/types';
import { useNoticeDataAffidavitSettings } from 'lib/frontend/hooks/useNoticeDataAffidavitSettings';
import { useAppSelector } from 'redux/hooks';
import {
  selectFiledBy,
  selectFiler,
  selectNewspaper,
  selectNoticeType,
  selectPreviousNoticeType
} from 'redux/placement';
import { getFirebaseContext } from 'utils/firebase';
import { ADDONS_COPY } from './addOnsCopy';
import { formatCents } from './pricingConfig';

type DeliveryPricingSummaryProps = {
  mailRecipients: MailDelivery[];
  config: NoticeAddOnPricingConfig;
};

/** Live, recomputed-on-every-render add-on price summary using the real pricing engine. */
export function DeliveryPricingSummary({
  mailRecipients,
  config
}: DeliveryPricingSummaryProps) {
  const placementNewspaper = useAppSelector(selectNewspaper);
  const placementFiler = useAppSelector(selectFiler);
  const placementFiledBy = useAppSelector(selectFiledBy);
  const placementNoticeType = useAppSelector(selectNoticeType);
  const placementPreviousNoticeType = useAppSelector(selectPreviousNoticeType);

  /** The same cascade the BE invoice reads, so the preview cannot drift from the bill. */
  const {
    settings: affidavitSettings,
    loading: affidavitSettingsLoading,
    error: affidavitSettingsError
  } = useNoticeDataAffidavitSettings(
    getFirebaseContext(),
    placementNewspaper
      ? {
          newspaper: placementNewspaper,
          filer: placementFiler ?? undefined,
          filedBy: placementFiledBy ?? undefined,
          noticeType: placementNoticeType,
          previousNoticeType: placementPreviousNoticeType
        }
      : undefined
  );

  // Until the cascade resolves, any price computed here could disagree with
  // what the invoice will actually bill — show nothing rather than a guess.
  // The publisher layer alone is such a guess: it misses the customer and
  // customer-organization overrides the invoice will apply.
  if (affidavitSettingsLoading || affidavitSettingsError) {
    return null;
  }

  const context = deriveNoticeAddOnPricingContext(affidavitSettings);
  const [err, addOns] = computeNoticeAddOnPricing(
    mailRecipients,
    config,
    context
  );

  // Same expansion the invoice uses, so the preview can't drift from the bill.
  const items = err ? [] : expandNoticeAddOnsToLineItems(addOns, new Date());
  const total = items.reduce((sum, i) => sum + i.amount, 0);

  return (
    <Card>
      <div className="space-y-2">
        <h3 className="text-sm font-bold text-column-gray-800">
          {ADDONS_COPY.pricingSummaryTitle}
        </h3>
        {items.length === 0 ? (
          <p className="text-sm text-column-gray-400">
            {ADDONS_COPY.pricingSummaryEmpty}
          </p>
        ) : (
          <>
            <div className="divide-y divide-column-gray-100">
              {items.map((item, i) => (
                <div
                  key={`${item.description}-${i}`}
                  className="flex justify-between text-sm py-1 text-column-gray-500"
                >
                  <span>{item.description}</span>
                  <span>{formatCents(item.amount)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between text-sm font-bold text-column-gray-800 pt-2 border-t border-column-gray-200">
              <span>{ADDONS_COPY.pricingSummaryTotal}</span>
              <span>{formatCents(total)}</span>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
